import { State, Action, StateContext, Selector, Store, createSelector } from '@ngxs/store';
import { StoryLabelsStateModel, ContextLabelId } from './story.labels.state.model';
import { 
  AddLabel,
  RemoveLabel,
  SetLabels,
  SelectLabel,
  SetLabel,
  RemoveAllChapterLabels,
  CopyLabels,
  ResetLabels,
  ConvertLabels,
  AddContextLabel,
  EnableContextLabel,
  RemoveContextLabel,
  SetLabelsShown,
  SetAllLabelsShown,
  SetLabelsCollapsed,
  SetLabelsDraggable,
  PatchLabels,
  SetLabelOffset,
  SetLabelStates,
  SetLabelScale} from './story.labels.actions';
import { uuid } from 'src/app/shared/uuid';
import { StoryChaptersState } from '../chapters/story.chapters.state';
import { StoryChaptersStateModel } from '../chapters/story.chapters.state.model';
import { LabelsService } from '../../labels.service';
import { cloneDeep, partition } from 'lodash';
import { LabelCollapseModeEnum } from '../../models/labels/label-collapse-mode';

import { UndoActions } from 'src/app/undo/state/undo.state.actions';
import { Injectable } from '@angular/core';
import { LabelPinType } from '../../models/labels/label-pin-type.enum';
import { LabelSequentialType } from '../../models/labels/label-sequential-type.enum';
import { LabelModel } from '../../models/labels/label.model';

declare const immer;
declare const Human: any;

const initialState: StoryLabelsStateModel = {
  enableContextLabel: {
    enable: true,
    enableInfo: true,
    enableActions: true
  },
  loading: false,
  labelsCollapsed: false,
  labelsCollapsedMode: LabelCollapseModeEnum.None,
  labelScale: null,
  labels: [],
  contextLabel: null,
  selectedLabel: null,
  draggable: {
    pin: true,
    tag: true
  }
};

@State<StoryLabelsStateModel>({
  name: 'labels',
  defaults: initialState
})
@Injectable()
export class StoryLabelsState {
  /**
   * Selectors
   */
  @Selector()
  static getLabels(state: StoryLabelsStateModel) {
    return state.labels;
  }

  @Selector()
  static getLabelsCollapsed(state: StoryLabelsStateModel) {
    return state.labelsCollapsed;
  }

  @Selector()
  static getLabelsCollapsedMode(state: StoryLabelsStateModel) {
    return state.labelsCollapsedMode;
  }

  @Selector()
  static getSelectedLabelId(state: StoryLabelsStateModel) {
    return state.selectedLabel;
  }

  @Selector()
  static getSelectedLabel(state: StoryLabelsStateModel) {
    return state.labels.find(label => label.id === state.selectedLabel) || null;
  }

  @Selector()
  static getContextLabel(state: StoryLabelsStateModel) {
    return state.contextLabel;
  }

  @Selector([StoryChaptersState])
  static getChapterLabels(state: StoryLabelsStateModel, storyChaptersState: StoryChaptersStateModel) {
    return state.labels.filter(label => label.chapterId === storyChaptersState.selectedChapter);
  }

  static getChapterSequentialLabels(type: LabelSequentialType) {
    return createSelector([StoryLabelsState, StoryChaptersState], (state: StoryLabelsStateModel, storyChaptersState: StoryChaptersStateModel) => {
      return state.labels.filter(label => 
        (label.chapterId === storyChaptersState.selectedChapter) && 
        (label.pinType === LabelPinType.Sequential) && 
        (label.sequentialType === type));
    });
  }
  
  static getLabelSequence(label: LabelModel) {
    return createSelector([
      StoryLabelsState, 
      StoryLabelsState.getChapterSequentialLabels(LabelSequentialType.Alphabetical), 
      StoryLabelsState.getChapterSequentialLabels(LabelSequentialType.Numerical)
    ], (state: StoryLabelsState, alphabetical: LabelModel[], numerical: LabelModel[]) => {
      if (label.pinType !== LabelPinType.Sequential) {
        return null;
      }

      switch (label.sequentialType) {
        case LabelSequentialType.Alphabetical:
          const i = alphabetical.findIndex((value) => value.id === label.id); 
          return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.charAt(i);

        case LabelSequentialType.Numerical: 
          return numerical.findIndex((value) => value.id === label.id) + 1;
      }
    });
  }

  @Selector()
  static getEnableContextLabel(state: StoryLabelsStateModel) {
    return state.enableContextLabel.enable;
  }

  @Selector()
  static getEnableContextLabelInfo(state: StoryLabelsStateModel) {
    return state.enableContextLabel.enableInfo;
  }

  @Selector()
  static getEnableContextLabelActions(state: StoryLabelsStateModel) {
    return state.enableContextLabel.enableActions;
  }

  @Selector()
  static getLabelsDraggable(state: StoryLabelsStateModel) {
    return state.draggable;
  }

  @Selector()
  static getLabelScale(state: StoryLabelsStateModel) {
    return state.labelScale;
  }

  /**
   * Actions
   */

  @Action(PatchLabels)
  PatchLabels(context: StateContext<StoryLabelsStateModel>, action: PatchLabels) {
    const nextState = immer.applyPatches(context.getState(), action.patches);
    context.patchState(nextState);
  }

  @Action(SetLabelOffset)
  setLabelOffset(context: StateContext<StoryLabelsStateModel>, action: SetLabelOffset) {
    const [nextState, patches, inversePatches] = immer.produceWithPatches(context.getState(), draft => {
      const label = draft.labels.find(_label => _label.id === action.labelId);
      if (label) {
        label.offset = action.offset;
      }
    });
    context.patchState(nextState);
    this.savePatches(patches, inversePatches);
  }


  @Action(SetLabels)
  setLabels(context: StateContext<StoryLabelsStateModel>, action: SetLabels) {
    context.patchState(immer.produce(context.getState(), draft => {
      draft.labels = action.labels;
    }));
  }

  @Action(SetLabel)
  setLabel(context: StateContext<StoryLabelsStateModel>, action: SetLabel) {
    const [nextState, patch, inversePatches] = immer.produceWithPatches(
      context.getState(),
      draft => {
        draft.labels = draft.labels.map(label => {
          return label.id === action.label.id ? action.label : label;
        });
      }
    );
    context.patchState(nextState);
    if (action.record) {
      this.savePatches(patch, inversePatches);
    }
  }

  @Action(SetLabelStates)
  setLabelStates(context: StateContext<StoryLabelsStateModel>, action: SetLabelStates) {
    const [nextState, patch, inversePatches] = immer.produceWithPatches(
      context.getState(),
      draft => {
        const label = draft.labels.find(_label => _label.id === action.id);
        label.states = action.states;
      }
    );

    context.patchState(nextState);
    this.savePatches(patch, inversePatches);
  }

  @Action(SetLabelScale)
  setLabelScale(context: StateContext<StoryLabelsStateModel>, action: SetLabelScale) {
    context.patchState({
      labelScale: action.scale
    });
  }

  @Action(AddLabel)
  addLabel(context: StateContext<StoryLabelsStateModel>, action: AddLabel) {
    const [nextState, patches, inversePatches] = immer.produceWithPatches(context.getState(), draft => {
      const labelId = action.label.id || uuid.idWithPrefix('label');
      const label = {...action.label, id: labelId};
      draft.labels.push(label);
    });
    context.patchState(nextState);
    this.savePatches(patches, inversePatches);
  }

  @Action(AddContextLabel)
  addContextLabel(context: StateContext<StoryLabelsStateModel>, action: AddContextLabel) {
    const nextState = immer.produce(context.getState(), draft => {
      draft.contextLabel = {...action.label, id: ContextLabelId};
    });
    context.patchState(nextState);
  }


  @Action(CopyLabels)
  copyLabels(context: StateContext<StoryLabelsStateModel>, action: CopyLabels) {
    const labels = context.getState().labels;
    const sourceLabels = labels.filter(label => label.chapterId === action.sourceChapter.id);
    const targetLabels = sourceLabels.map(label => {
      return {
        ...(cloneDeep(label)),
        id: uuid.idWithPrefix('label'),
        chapterId: action.targetChapter.id
      };
    });

    context.patchState({
      labels: [...labels, ...targetLabels]
    });
  }
  
  @Action(RemoveLabel)
  removeLabel(context: StateContext<StoryLabelsStateModel>, action: RemoveLabel) {

    const [nextState, patches, inversePatches] = immer.produceWithPatches(context.getState(), draft => {
      draft.labels = draft.labels.filter(label => label.id !== action.label.id);
    });

    context.patchState(nextState);
    Human.scene.markers.delete(action.label.id);
    this.savePatches(patches, inversePatches);
  }

  @Action(RemoveContextLabel)
  removeContextLabel(context: StateContext<StoryLabelsStateModel>, action: RemoveContextLabel) {
    const nextState = immer.produce(context.getState(), draft => {
      draft.contextLabel = undefined;
    });
    Human.scene.markers.delete(ContextLabelId);
    context.patchState(nextState);
  }

  @Action(RemoveAllChapterLabels)
  removeAllChapterLabels(context: StateContext<StoryLabelsStateModel>, action: RemoveAllChapterLabels) {
    const [nextState, patches, inversePatches] = immer.produceWithPatches(context.getState(), draft => {
      const [keep, throwAway] = partition(draft.labels, (label => label.chapterId !== action.chapter.id));
      throwAway.forEach(label => Human.scene.markers.delete(label.id));
      draft.labels = keep;
    });
    context.patchState(nextState);
    this.savePatches(patches, inversePatches);
  }

  @Action(SelectLabel)
  selectLabel(context: StateContext<StoryLabelsStateModel>, action: SelectLabel) {
    context.patchState({
      selectedLabel: action.label ? action.label.id : null
    });
  }

  @Action(ResetLabels)
  resetLabels(context: StateContext<StoryLabelsStateModel>, action: ResetLabels) {
    const state = context.getState();

    Human.scene.markers.delete(ContextLabelId);
    state.labels.forEach((label) => {
      Human.scene.markers.delete(label.id);
    });

    context.setState(immer.produce(context.getState(), draft => {
      draft = initialState;
    }));
  }

  @Action(ConvertLabels)
  convertLabels(context: StateContext<StoryLabelsStateModel>, action: ConvertLabels) {
    const [nextState, patches, inversePatches] = immer.produceWithPatches(context.getState(), draft => {
      action.labels.forEach(label => {
        const index = draft.labels.findIndex(_label => _label.id === label.id);
        // draft.labels[index].theme = action.theme;
        draft.labels[index] = {
          ...draft.labels[index],
          ...action.fields
        };
      });
    });

    context.setState({...nextState});
    this.savePatches(patches, inversePatches);
  }

  @Action(EnableContextLabel)
  enableContextLabel(context: StateContext<StoryLabelsStateModel>, action: EnableContextLabel) {
    context.patchState({
      enableContextLabel: {
        ...action
      }
    });
  }
  
  @Action(SetLabelsShown)
  setLabelsShown(context: StateContext<StoryLabelsStateModel>, { labels: actionLabels, shown }: SetLabelsShown) {
    // set shown state for each update label
    const labels = context.getState().labels
      .map((label) => {
        const doUpdate = actionLabels.some((actionLabel) => actionLabel.id === label.id);
        return {
          ...label,
          shown: doUpdate ? shown : label.shown
        };
      });

    context.patchState({
      labels: [...labels]
    });
  }

  @Action(SetAllLabelsShown)
  setAllLabelsShown(context: StateContext<StoryLabelsStateModel>, { show }: SetAllLabelsShown) {
    const labels = context.getState().labels;
    context.dispatch(new SetLabelsShown(labels, show));
  }

  
  @Action(SetLabelsCollapsed)
  setLabelsCollapsed(context: StateContext<StoryLabelsStateModel>, { collapsed, collapsedMode: mode }: SetLabelsCollapsed) {
    context.patchState({
      labelsCollapsed: collapsed,
      labelsCollapsedMode: mode
    });
  }

  @Action(SetLabelsDraggable)
  setLabelsDraggable(context: StateContext<StoryLabelsStateModel>, action: SetLabelsDraggable) {
    context.patchState({
      draggable: {...action.draggable}
    });
  }

  private savePatches(patches, inversePatches) {
    this.store.dispatch(
      new UndoActions.SavePatches([
        {
          forward: patches,
          inverse: inversePatches,
          patchAction: PatchLabels,
        },
      ])
    );
    
  }

  constructor(
    private store: Store,
    private labelsService: LabelsService
  ) { }
}
